import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Company } from '../../../interfaces/entities/company';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyForm } from './companyForm';
import { defaultCompany } from '../../../defaultValues/defaultCompany';
import { LoadingIndicator } from '../../shared/alerts/loadingIndicator';
import { ErrorDisplay } from '../../shared/alerts/errorDisplay';
import { useCreateCompanyMutation, useGetCompanyByIdQuery, useUpdateCompanyMutation } from '../../../api/companyApi';
import { useToast } from '../../../hooks/useToast';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';
import { useLoader } from '../../../hooks/useLoader';
import { getCompanyImagePath } from '../../../services/storageService';
import { uploadImage } from '../../../services/imageUploaderService';

export const CompanyAddContainer: React.FC = () => {
    const { user, isAdmin } = useAuthenticatedUser();
    const { companyId } = useParams<{ companyId?: string }>(); // Mark id as optional
    const compId = companyId ?? ""; // Ensure id is always a string
    const [companyImage, setCompanyImage] = useState<File | null>(null);

    const navigate = useNavigate();
    
    const { data: company, isLoading: loadingCompanies, error } = useGetCompanyByIdQuery(compId || "");
    const [createCompany, createProcess] = useCreateCompanyMutation();
    const [updateCompany, updateProcess] = useUpdateCompanyMutation();

    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    const handleImageChange = (file: File | null) => {
        setCompanyImage(file);
    };

    const handleClose = () => {};

    // Handle form submission
    const handleFormSubmit = async (values: Company) => {
        try {
            showLoader();
            const companyData = { ...values };

            const createdCompany = await createCompany(companyData).unwrap();

            if (createdCompany) {
                if (companyImage) {
                    const imageUrl = await uploadImage(getCompanyImagePath(createdCompany.id), companyImage);
                    await updateCompany({ companyId: createdCompany.id, data: { image: imageUrl } }).unwrap();
                }
            } else {
                showError("Failed to create company 2");
            }

            showSuccess("Company created successfully!");
            navigate(-1); // Navigate back to previous screen
        } catch (error: any) {
            console.error('Error while updating company:', error);
            showError(`${error.message}`);
        } finally {
            hideLoader();
        }
    };

    const isLoading = createProcess.isLoading || updateProcess.isLoading || loadingCompanies;

    if (isLoading) return <LoadingIndicator message="Loading company data..." />;
    if (error) return <ErrorDisplay message="Error fetching company data." />;
    if (!user || !isAdmin) return <div>Unauthorized</div>;

    // Include roles in initialValues
    const initialValues: Company = {
        ...defaultCompany(user.id),
        ...(company || {}),
    };

    return (
        <Box>
            <CompanyForm
                title='Create New Company'
                isSubmitting={isLoading}
                onSubmit={handleFormSubmit}
                initialValues={initialValues}
                onImageChange={handleImageChange}
                onClose={handleClose}
            />
        </Box>
    );
};
